<script setup lang="ts">
import { useBootLoader, useFirstRouteRendered } from "@verbleif/lib/src/components/BrandLoader/useBootLoader";
import DialogHost from "@verbleif/lib/src/components/Dialog/DialogHost.vue";
import VSidebarMobileBar from "@verbleif/lib/src/components/Sidebar/VSidebarMobileBar.vue";
import { localeState, updateLocale } from "@verbleif/lib/src/translations/localeState";
import { defineDebugSection } from "@verbleif/shared/src/components/DebugTools/defineDebugSection";
import { sharedDebugSections } from "@verbleif/shared/src/components/DebugTools/sections/sharedDebugSections";
import { useDebugConfigAdapter } from "@verbleif/shared/src/components/DebugTools/useDebugConfigAdapter";
import { useDebugTools } from "@verbleif/shared/src/components/DebugTools/useDebugTools";
import AppToaster from "@verbleif/shared/src/components/Sonner/AppToaster.vue";
import { AI_TRANSLATION_MODULE_KEY, useAiTranslationModulePreference } from "@verbleif/shared/src/composables/Ai/useAiTranslationModule";
import { useAppUpdate } from "@verbleif/shared/src/composables/AppUpdate/useAppUpdate";
import { useAppUpdateDialog } from "@verbleif/shared/src/composables/AppUpdate/useAppUpdateDialog";
import { isOAuthCallbackWithCode } from "@verbleif/shared/src/composables/AuthStore/oauthCallback";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { DESCRIPTION_HINT_MODULE_KEY, useDescriptionHintModulePreference } from "@verbleif/shared/src/composables/DescriptionHints/useDescriptionHintModuleEnabled";
import { MULTILINGUAL_MODULE_KEY, useMultilingualModulePreference } from "@verbleif/shared/src/composables/FieldTranslation/useMultilingualModule";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { useNotificationClickMessages } from "@verbleif/shared/src/composables/Notifications/useNotificationClickMessages";
import { useReadNotificationFromLink } from "@verbleif/shared/src/composables/Notifications/v2/useReadNotificationFromLink";
import { isAdmin, usePermsWatch } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { useLocaleQueryReset } from "@verbleif/shared/src/composables/Query/useLocaleQueryReset";
import { useResourceCatalog } from "@verbleif/shared/src/composables/Query/useResourceCatalog";
import { useViewManagementModulePreference, VIEW_MANAGEMENT_MODULE_KEY } from "@verbleif/shared/src/composables/ScopedViews/useViewManagementModule";
import { useSystemStore } from "@verbleif/shared/src/composables/SystemStore/useSystemStore";
import { useTheme } from "@verbleif/shared/src/composables/Theme/Theme";
import { useThemePreferenceSync } from "@verbleif/shared/src/composables/Theme/themePreference";
import { useFirebase } from "@verbleif/shared/src/composables/useFirebase";
import { useSyncEffectiveUserPreferencesWithLocation } from "@verbleif/shared/src/composables/Users/UserPreferences/refreshEffectiveUserPreferences";
import { setYupLocales } from "@verbleif/shared/src/plugins/yupLocales";
import { refreshToken } from "@web/core/refresh";
import { isScreenshotMode } from "@web/core/runtimeFlags";
import { useLoader } from "@web/features/Loader/useLoader";
import MobileTabBar from "@web/features/Navbar/MobileTabBar.vue";
import NavbarContainer from "@web/features/Navbar/NavbarContainer.vue";
import Notifications from "@web/features/Notification/NotificationList.vue";
import moment from "moment";
import { computed, provide, ref, watch } from "vue";
import router from "./core/plugin/router";
import { useImpersonateFailedDialog } from "./core/useImpersonateFailedDialog";
import { useActiveTimeEntry } from "./features/Tasks/TimeEntry/useActiveTimeEntry";
import "@web/core/impersonate";
import "./GleapTools";

useDebugTools({
  app: "web",
  config: useDebugConfigAdapter(),
  admin: isAdmin,
  sections: [
    ...sharedDebugSections,
    defineDebugSection({
      id: "cache",
      label: "debug_tools.sections.cache",
      icon: "layers",
      component: () => import("./features/DebugTools/PropertyStateSection.vue"),
      order: 70,
    }),
  ],
});
useImpersonateFailedDialog();
const { getActiveTimeEntry } = useActiveTimeEntry();
const { t } = useI18n();
const { user } = useAuthStore();
const route = useRoute();

useLocaleQueryReset();
useSyncEffectiveUserPreferencesWithLocation();
provide(AI_TRANSLATION_MODULE_KEY, useAiTranslationModulePreference());
provide(VIEW_MANAGEMENT_MODULE_KEY, useViewManagementModulePreference());
provide(DESCRIPTION_HINT_MODULE_KEY, useDescriptionHintModulePreference());
provide(MULTILINGUAL_MODULE_KEY, useMultilingualModulePreference());

if (!isScreenshotMode) {
  watch(() => user.value?.locale, (loc) => {
    // Without a loaded user there is no locale to follow. Falling back to "nl" here made every other-language user
    // flip nl -> own language once the user arrived, and that flip resets the queries mid catalog load.
    if (route.meta.skipAuthChecks !== true && loc) {
      updateLocale(loc.toLowerCase());
      moment.locale(localeState.value);
    }
    setYupLocales(t);
  }, { immediate: true });
} else {
  setYupLocales(t);
}

if (!isScreenshotMode) {
  useFirebase();
  useReadNotificationFromLink();
  useNotificationClickMessages();
}

const { init: initAppUpdate } = useAppUpdate();
initAppUpdate(router, () => import("virtual:pwa-register/vue"));
useAppUpdateDialog();

const authStore = useAuthStore();
const systemStore = useSystemStore();
const locationStore = useLocationStore();
const loaderProvider = useLoader();
useResourceCatalog({
  route,
  include: "full",
  getActiveTimeEntry,
});

const hideNavbar = computed(
  () => route.path === "/" || route.meta.hideNavbar === true,
);
const isPublicRoute = computed(
  () => route.path === "/" || route.meta.public === true,
);
const globalLoading = ref(true);

// Reset system store when location changes
watch(() => locationStore.selectedLocation.value, (newLocation, oldLocation) => {
  if (!newLocation) {
    return;
  }
  // When we reload the page, oldLocation is undefined. We skip because this causes the task + reports filters to be reset everytime we reload the page.
  if (typeof oldLocation === "undefined") {
    return;
  }
  console.log("%c [App.vue] locationStore.selectedLocation changed / immediate to: ", "color: #4CAF50; font-weight: bold;", newLocation);
  systemStore.reset();
}, { immediate: true });

if (!authStore.hasToken.value && !authStore.isRemembered.value) {
  authStore.setUser(null); // Reset it to be sure.
  globalLoading.value = false;
  loaderProvider.hide();
}

// The OAuth callback exchanges its own code for a fresh pair. Refreshing a remembered token first would run a
// token the callback is about to replace, and a rejected one would send the user away before the code is used.
if (authStore.hasToken.value || authStore.isRemembered.value) {
  if (isOAuthCallbackWithCode()) {
    globalLoading.value = false;
    loaderProvider.hide();
  } else {
    refreshToken()
      .finally(() => {
        globalLoading.value = false;
        loaderProvider.hide();
      });
  }
}

const loading = computed(() => loaderProvider.loading.value);
const firstRouteRendered = useFirstRouteRendered(router);
useBootLoader(() => loading.value || globalLoading.value || !firstRouteRendered.value);
useTheme();
useThemePreferenceSync();

usePermsWatch();
</script>

<template>
  <Notifications v-if="!isScreenshotMode" />
  <AppToaster v-if="!isScreenshotMode" />
  <DialogHost />
  <template v-if="!globalLoading">
    <template v-if="!isPublicRoute && !hideNavbar">
      <NavbarContainer />
      <MobileTabBar />
    </template>
    <div class="app-container" :class="{ 'is-login-route': !hideNavbar }">
      <VSidebarMobileBar />
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </div>
  </template>
</template>

<style src="./tailwind.css" />

<style lang="scss" src="./main.scss" />
